<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div class="row justify-content-center">
      <div class="col-sm-12 col-xs-12">
        <div class="pb-0 col-sm-12 d-flex mainDiv">
          <div class="col-sm-12 col-xs-12">

            <!-- 페이지 타이틀 영역 -->
            <section class="page-title-section">
              <div class="title-content">
                <h3>
                  인증서 시술 관리
                  <a-tooltip placement="right" overlayClassName="page-help-tooltip">
                    <template #title>
                      <div class="tooltip-content">
                        <div class="tooltip-section">
                          <div class="tooltip-title">영향받는 DB 컬렉션</div>
                          <div class="tooltip-body">
                            <ul>
                              <li><code>items</code> - 품목 등록/수정/삭제, displayOrder 변경, tagImageUrl 변경</li>
                              <li><code>itemhospitals</code> - 병원별 시술 연결 정보 (병원이 해당 시술을 사용할 때 참조)</li>
                            </ul>
                          </div>
                        </div>
                        <div class="tooltip-section">
                          <div class="tooltip-title">영향받는 화면 (web-customer)</div>
                          <div class="tooltip-body">
                            <ul>
                              <li><strong>병원 목록 페이지</strong> - 병원 카드의 시술 태그 (태그 이미지, 순서)</li>
                              <li><strong>홈 화면</strong> - 추천 병원 카드의 시술 태그</li>
                              <li><strong>병원 상세 페이지</strong> - 시술 정보 표시</li>
                            </ul>
                          </div>
                        </div>
                        <div class="tooltip-section">
                          <div class="tooltip-title">영향받는 화면 (admin-hospital)</div>
                          <div class="tooltip-body">
                            <ul>
                              <li><strong>인증서 발행 페이지</strong> - 시술 품목 선택 및 인증서 이미지 표시</li>
                            </ul>
                          </div>
                        </div>
                        <div class="tooltip-section">
                          <div class="tooltip-title">동일 기능 제공 페이지</div>
                          <div class="tooltip-body">
                            <strong>admin-manufacturer</strong> (제조사 관리자 페이지)의 <strong>병원 관리</strong> 메뉴에서도 동일한 시술 등록/수정 작업을 수행할 수 있습니다. 제조사 담당자가 직접 자사 시술 품목을 관리할 때 사용합니다.
                          </div>
                        </div>
                      </div>
                    </template>
                    <i class="fa-solid fa-circle-question help-icon" />
                  </a-tooltip>
                </h3>
                <h5>인증서를 발행할 시술 항목을 등록하고 관리할 수 있습니다.</h5>
              </div>
            </section>

            <!-- 필터 영역 -->
            <section class="filter-section">
              <div class="filter-row">
                <div class="filter-content">
                  <div class="filter-item">
                    <label class="filter-label">제조사</label>
                    <a-select
                      v-model:value="selectedManufacturer"
                      style="width: 200px;"
                      placeholder="제조사 선택"
                      @change="handleManufacturerChange"
                    >
                      <a-select-option
                        v-for="manufacturer in manufacturers"
                        :key="manufacturer._id"
                        :value="manufacturer._id"
                      >
                        {{ manufacturer.name }}
                      </a-select-option>
                    </a-select>
                  </div>
                  <div class="filter-item">
                    <label class="filter-label">검색</label>
                    <div class="search-box">
                      <a-select
                        v-model:value="searchFilter"
                        class="search-filter-select"
                        placeholder="선택"
                        style="width: 100px"
                      >
                        <a-select-option value="">선택</a-select-option>
                        <a-select-option value="itemName">품목명</a-select-option>
                        <a-select-option value="itemCode">품목 코드</a-select-option>
                      </a-select>
                      <a-input
                        placeholder="검색어를 입력하세요."
                        v-model:value="searchCondition"
                        @keyup.enter="handleSearch"
                        class="search-input"
                        style="width: 200px;"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="filter-actions">
                <a-button type="primary" @click="handleSearch" class="yellow-button">
                  검색
                </a-button>
                <a-button @click="handleReset">
                  초기화
                </a-button>
              </div>
            </section>

            <!-- 컨트롤 영역 -->
            <section class="control-section">
              <div class="control-left">
                <p class="total-count">
                  전체 <span class="number-highlight">{{ formatCommaNumber(listSize) }}</span>건
                </p>
              </div>
              <div class="control-right">
                <a-button
                  v-if="!isOrderMode"
                  @click="enterOrderMode"
                >
                  <i class="fa-solid fa-sort"/>
                  순서 관리
                </a-button>
                <a-button
                  v-if="isOrderMode"
                  @click="cancelOrderMode"
                >
                  취소
                </a-button>
                <a-button
                  v-if="isOrderMode"
                  type="primary"
                  :loading="orderSaving"
                  @click="saveDisplayOrder"
                  class="yellow-button"
                >
                  순서 저장
                </a-button>
                <a-button
                  v-if="!isOrderMode"
                  type="primary"
                  @click="handleAddItem"
                >
                  <i class="fa-regular fa-plus"/>
                  시술 등록
                </a-button>
              </div>
            </section>

            <!-- 조회 영역 -->
            <section class="list-section">
              <!-- 일반 테이블 뷰 -->
              <a-table
                v-if="!isOrderMode"
                class="ant-table-striped"
                size="small"
                :columns="columns"
                :data-source="responseData"
                :pagination="false"
                :scroll="{ x: 1100 }"
              >
                <template #emptyText>
                  <div class="noData">
                    <i class="fa-solid fa-exclamation-triangle" style="font-size: 24px; margin-bottom: 10px"/><br>
                    <span>{{ computedNoResultMessage }}</span>
                  </div>
                </template>
              </a-table>

              <!-- 순서 관리 모드: 드래그앤드롭 뷰 -->
              <div v-if="isOrderMode" class="order-mode-container">
                <div class="order-mode-header">
                  <p class="order-mode-info">
                    <i class="fa-solid fa-info-circle" />
                    항목을 드래그하여 순서를 변경하세요. 변경 후 '순서 저장' 버튼을 클릭하세요.
                  </p>
                </div>
                <draggable
                  v-model="orderModeData"
                  item-key="_id"
                  handle=".drag-handle"
                  @start="onDragStart"
                  @end="onDragEnd"
                  class="draggable-list"
                >
                  <template #item="{ element, index }">
                    <div class="draggable-item" :class="{ 'is-dragging': draggingItem === element._id }">
                      <div class="drag-handle">
                        <i class="fa-solid fa-grip-vertical" />
                      </div>
                      <div class="item-order">{{ index + 1 }}</div>
                      <div class="item-image">
                        <img
                          v-if="element.certificateImgUrl"
                          :src="element.certificateImgUrl"
                          alt=""
                        />
                        <span v-else class="no-image">-</span>
                      </div>
                      <div class="item-info">
                        <div class="item-name">{{ element.itemName || '-' }}</div>
                        <div class="item-meta">
                          <span class="item-code">{{ element.itemCode || '-' }}</span>
                          <span class="item-type">{{ element.productType || '-' }}</span>
                        </div>
                      </div>
                      <div class="item-display-order">
                        displayOrder: {{ element.displayOrder ?? 100 }}
                      </div>
                    </div>
                  </template>
                </draggable>
              </div>

              <!-- 페이지네이션 영역 -->
              <div
                v-if="!$store.state.isLoading && responseData.length > 0 && !isOrderMode"
                class="pagination-section"
              >
                <a-pagination
                  v-model:current="paginationData.currentPage"
                  :total="listSize"
                  :pageSize="rowsPerPage"
                  :showSizeChanger="false"
                  @change="handlePageChange"
                />
                <a-select
                  v-model:value="rowsPerPage"
                  @change="handleRowsPerPage"
                  class="rows-per-page-select"
                >
                  <a-select-option :value="10">10개씩 보기</a-select-option>
                  <a-select-option :value="20">20개씩 보기</a-select-option>
                  <a-select-option :value="50">50개씩 보기</a-select-option>
                </a-select>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>

    <!-- 기본 템플릿 내용 보기 모달 -->
    <a-modal
      v-model:visible="templateModalVisible"
      :title="templateModalTitle"
      :footer="null"
      width="600px"
      @cancel="closeTemplateModal"
    >
      <div class="template-modal-content">
        <div class="template-info-box">
          <div class="info-item">
            <i class="fa-solid fa-database" />
            <span>
              <strong>데이터 출처:</strong> items 컬렉션 (제조사가 시술 등록 시 입력)
            </span>
          </div>
          <div class="info-item">
            <i class="fa-solid fa-info-circle" />
            <span>
              병원에서 별도 템플릿을 등록하지 않은 경우, 이 값이 기본값으로 사용됩니다.
            </span>
          </div>
        </div>
        <div class="template-content-box">
          <pre class="template-text">{{ templateModalContent || '(내용 없음)' }}</pre>
        </div>
      </div>
    </a-modal>

    <!-- 병원 권한 관리 모달 -->
    <a-modal
      v-model:visible="hospitalModalVisible"
      title="병원 권한 관리"
      :confirm-loading="hospitalModalLoading"
      @cancel="closeHospitalModal"
      width="700px"
      :footer="null"
    >
      <div class="hospital-modal-content">
        <div class="modal-header">
          <p>
            <strong>{{ selectedItemName }}</strong> 시술의 병원 권한을 관리합니다.
          </p>
        </div>

        <!-- 탭 -->
        <a-tabs v-model:activeKey="hospitalModalTab" @change="handleHospitalTabChange">
          <!-- 권한 부여 탭 -->
          <a-tab-pane key="grant" tab="권한 부여">
            <!-- Poka-Yoke: 기본 템플릿 미등록 시 경고 -->
            <a-alert
              v-if="!selectedItemHasDefaultTemplate"
              type="warning"
              show-icon
              style="margin-bottom: 16px;"
            >
              <template #message>
                <strong>기본 템플릿이 등록되지 않았습니다</strong>
              </template>
              <template #description>
                권한을 부여하기 전에 먼저 시술 정보 페이지에서 "치료 후 주의사항"을 등록해주세요.
                기본 템플릿이 없으면 병원에서 인증서 발행 시 주의사항이 비어있게 됩니다.
              </template>
            </a-alert>

            <!-- 권한 부여 시 영향 안내 -->
            <a-alert
              v-if="selectedItemHasDefaultTemplate"
              type="info"
              show-icon
              style="margin-bottom: 16px;"
            >
              <template #message>
                권한 부여 시 기본 템플릿이 자동으로 복사됩니다
              </template>
              <template #description>
                선택한 병원에 "치료 후 주의사항"과 "보증사항" 기본 템플릿이 자동으로 설정됩니다.
                병원은 이후 자체적으로 내용을 수정할 수 있습니다.
              </template>
            </a-alert>

            <!-- 선택된 병원 목록 섹션 -->
            <div class="selected-hospitals-section" v-if="selectedHospitalsMap.size > 0">
              <div class="selected-hospitals-header">
                <span class="selected-title">
                  <i class="fa-solid fa-check-circle" />
                  선택된 병원 ({{ selectedHospitalsMap.size }}개)
                </span>
                <a-button size="small" type="link" danger @click="clearAllSelectedHospitals">
                  전체 해제
                </a-button>
              </div>
              <div class="selected-hospitals-list">
                <a-tag
                  v-for="[id, hospital] in selectedHospitalsMap"
                  :key="id"
                  closable
                  @close="removeSelectedHospital(id)"
                  class="selected-hospital-tag"
                >
                  {{ hospital.name }}
                </a-tag>
              </div>
            </div>

            <!-- 검색 영역 -->
            <div class="hospital-search">
              <a-input-search
                v-model:value="hospitalSearchKeyword"
                placeholder="병원명으로 검색"
                @search="handleHospitalSearch"
                @pressEnter="handleHospitalSearch"
                style="width: 100%;"
                allowClear
              />
            </div>

            <div class="hospital-selection">
              <a-table
                :columns="hospitalColumns"
                :data-source="availableHospitals"
                :loading="hospitalModalLoading"
                :pagination="false"
                :row-selection="{ selectedRowKeys: computedSelectedHospitalIds, onChange: handleHospitalSelectionChange }"
                :rowKey="(record) => record._id"
                size="small"
              />
            </div>

            <!-- 페이지네이션 -->
            <div class="hospital-pagination" v-if="hospitalPagination.total > 0">
              <a-pagination
                v-model:current="hospitalPagination.page"
                :total="hospitalPagination.total"
                :pageSize="hospitalPagination.limit"
                :showSizeChanger="false"
                :showTotal="(total) => `전체 ${total}건`"
                @change="handleHospitalPageChange"
                size="small"
              />
            </div>

            <div class="modal-footer-buttons">
              <a-button @click="closeHospitalModal">취소</a-button>
              <a-tooltip
                :title="!selectedItemHasDefaultTemplate ? '기본 템플릿을 먼저 등록하시면 병원에 자동으로 설정됩니다' : '선택한 병원에 권한과 기본 템플릿이 부여됩니다'"
                placement="top"
              >
                <a-button
                  type="primary"
                  :loading="hospitalModalLoading"
                  :disabled="selectedHospitalsMap.size === 0"
                  @click="submitHospitalPermissions"
                  class="yellow-button"
                >
                  권한 부여 ({{ selectedHospitalsMap.size }}개)
                </a-button>
              </a-tooltip>
            </div>
          </a-tab-pane>

          <!-- 권한 관리 탭 -->
          <a-tab-pane key="manage" tab="권한 관리">
            <!-- 검색 영역 -->
            <div class="hospital-search">
              <a-input-search
                v-model:value="permittedSearchKeyword"
                placeholder="병원명으로 검색"
                @search="handlePermittedSearch"
                @pressEnter="handlePermittedSearch"
                style="width: 100%;"
                allowClear
              />
            </div>

            <div class="hospital-selection">
              <a-table
                :columns="permittedHospitalColumns"
                :data-source="permittedHospitals"
                :loading="hospitalModalLoading"
                :pagination="false"
                :row-selection="{ selectedRowKeys: selectedPermittedIds, onChange: handlePermittedSelectionChange }"
                :rowKey="(record) => record._id"
                size="small"
              />
            </div>

            <!-- 페이지네이션 -->
            <div class="hospital-pagination" v-if="permittedPagination.total > 0">
              <a-pagination
                v-model:current="permittedPagination.page"
                :total="permittedPagination.total"
                :pageSize="permittedPagination.limit"
                :showSizeChanger="false"
                :showTotal="(total) => `전체 ${total}건`"
                @change="handlePermittedPageChange"
                size="small"
              />
            </div>

            <div class="modal-summary" v-if="selectedPermittedIds.length > 0">
              <p>선택된 병원: <strong>{{ selectedPermittedIds.length }}</strong>개</p>
            </div>

            <div class="modal-footer-buttons">
              <a-button @click="closeHospitalModal">취소</a-button>
              <a-button
                type="primary"
                danger
                :loading="hospitalModalLoading"
                :disabled="selectedPermittedIds.length === 0"
                @click="removeHospitalPermissions"
              >
                권한 삭제
              </a-button>
            </div>
          </a-tab-pane>
        </a-tabs>
      </div>
    </a-modal>
  </div>
</template>

<script>
import {
  formatCommaNumber,
  oneBtnDialog,
} from '@/utils/utils';
import draggable from 'vuedraggable';

export default {
  name: 'CertificateSurgeryRegistration',
  components: {
    draggable,
  },
  computed: {
    computedNoResultMessage() {
      if (this.calledApiTimes <= 1) {
        return '아직 등록된 시술 항목이 없습니다';
      } else {
        return '검색 결과가 없습니다.';
      }
    },
    // 현재 페이지에서 선택된 병원 ID 목록 (체크박스 상태 반영용)
    computedSelectedHospitalIds() {
      return this.availableHospitals
        .filter(h => this.selectedHospitalsMap.has(h._id))
        .map(h => h._id);
    },
  },
  data() {
    return {
      formatCommaNumber,

      // 제조사 관련
      manufacturers: [],
      selectedManufacturer: null,

      // 목록 데이터
      responseData: [],
      listSize: 0,

      // 검색 관련
      searchFilter: undefined,
      searchCondition: '',
      calledApiTimes: 0,

      // 페이지네이션
      rowsPerPage: 10,
      paginationData: {
        currentPage: 1,
        totalPages: 0,
      },

      // 기본 템플릿 내용 보기 모달
      templateModalVisible: false,
      templateModalTitle: '',
      templateModalContent: '',

      // 병원 권한 관리 모달
      hospitalModalVisible: false,
      hospitalModalLoading: false,
      hospitalModalTab: 'grant',
      selectedItemId: null,
      selectedItemName: '',
      selectedItemHasDefaultTemplate: false, // Poka-Yoke: 기본 템플릿 등록 여부

      // 권한 부여 탭 (grant)
      availableHospitals: [],
      selectedHospitalsMap: new Map(), // 선택된 병원 정보를 Map으로 관리 (id -> hospital info)
      hospitalSearchKeyword: '',
      hospitalPagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },

      // 권한 관리 탭 (manage)
      permittedHospitals: [],
      selectedPermittedHospitals: [],
      selectedPermittedIds: [],
      permittedSearchKeyword: '',
      permittedPagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },

      // 병원 테이블 컬럼 (권한 부여용)
      hospitalColumns: [
        {
          key: 'name',
          title: '병원명',
          dataIndex: 'name',
          align: 'start',
          width: '50%',
        },
        {
          key: 'cNo',
          title: '사업자번호',
          dataIndex: 'cNo',
          align: 'center',
          width: '50%',
          customRender: ({ record }) => record.cNo || '-',
        },
      ],

      // 권한 병원 테이블 컬럼 (권한 관리용)
      permittedHospitalColumns: [
        {
          key: 'hospitalName',
          title: '병원명',
          dataIndex: 'hospitalName',
          align: 'start',
          width: '50%',
        },
        {
          key: 'cNo',
          title: '사업자번호',
          dataIndex: 'cNo',
          align: 'center',
          width: '50%',
          customRender: ({ record }) => record.cNo || '-',
        },
      ],

      // 순서 관리 모드 관련
      isOrderMode: false,
      orderModeData: [],
      orderSaving: false,
      draggingItem: null,

      // 테이블 컬럼 정의
      columns: [
        {
          key: 'displayOrder',
          title: '순서',
          dataIndex: 'displayOrder',
          align: 'center',
          width: 60,
          customRender: ({ record }) => record.displayOrder ?? 100,
        },
        {
          key: 'medicalDept',
          title: '과',
          dataIndex: 'medicalDept',
          align: 'center',
          width: 50,
        },
        {
          key: 'certificateImg',
          title: '인증서',
          dataIndex: 'certificateImgUrl',
          align: 'center',
          width: 70,
          customRender: ({ record }) => {
            if (!record.certificateImgUrl) {
              return <span style={{ color: '#9CA3AF' }}>-</span>;
            }
            return (
              <a-image
                src={record.certificateImgUrl}
                width={40}
                height={50}
                style={{ objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='50' viewBox='0 0 40 50'%3E%3Crect fill='%23f0f0f0' width='40' height='50'/%3E%3Ctext x='50%25' y='50%25' fill='%23999' font-size='10' text-anchor='middle' dy='.3em'%3E없음%3C/text%3E%3C/svg%3E"
              />
            );
          },
        },
        {
          key: 'tagImageUrl',
          title: '태그이미지',
          dataIndex: 'tagImageUrl',
          align: 'center',
          width: 80,
          customRender: ({ record }) => {
            if (!record.tagImageUrl) {
              return <span style={{ color: '#9CA3AF' }}>-</span>;
            }
            return (
              <a-image
                src={record.tagImageUrl}
                width={40}
                height={20}
                style={{ objectFit: 'contain', cursor: 'pointer' }}
                fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='20' viewBox='0 0 40 20'%3E%3Crect fill='%23f0f0f0' width='40' height='20'/%3E%3Ctext x='50%25' y='50%25' fill='%23999' font-size='8' text-anchor='middle' dy='.3em'%3E없음%3C/text%3E%3C/svg%3E"
              />
            );
          },
        },
        {
          key: 'itemName',
          title: '품목명',
          dataIndex: 'itemName',
          align: 'start',
          width: 200,
          ellipsis: true,
          customRender: ({ record }) => record.itemName || '-',
        },
        {
          key: 'productType',
          title: '제품분류',
          dataIndex: 'productType',
          align: 'start',
          width: 90,
          customRender: ({ record }) => record.productType || '-',
        },
        {
          key: 'itemCode',
          title: '품목코드',
          dataIndex: 'itemCode',
          align: 'start',
          width: 100,
          customRender: ({ record }) => record.itemCode || '-',
        },
        {
          key: 'defaultAfterSurgery',
          title: () => (
            <a-tooltip placement="top">
              {{
                title: () => (
                  <div style={{ fontSize: '12px', lineHeight: 1.5 }}>
                    <div><strong>데이터 출처:</strong> items 컬렉션</div>
                    <div style={{ marginTop: '4px' }}>병원이 contentstemplates 컬렉션에 별도 템플릿을 등록하지 않으면 이 값이 기본값으로 사용됩니다.</div>
                  </div>
                ),
                default: () => (
                  <span style={{ cursor: 'help', borderBottom: '1px dashed #9CA3AF', whiteSpace: 'nowrap' }}>
                    주의사항 <i class="fa-solid fa-circle-question" style={{ color: '#9CA3AF', marginLeft: '2px', fontSize: '11px' }}></i>
                  </span>
                ),
              }}
            </a-tooltip>
          ),
          dataIndex: 'defaultAfterSurgery',
          align: 'start',
          width: 100,
          customRender: ({ record }) => {
            const content = record.defaultAfterSurgery;
            if (!content) {
              return <span style={{ color: '#9CA3AF' }}>-</span>;
            }
            return (
              <a-tooltip title={content.length > 50 ? content.substring(0, 50) + '...' : content}>
                <a-button
                  type="link"
                  size="small"
                  onClick={() => this.openTemplateModal('치료 후 주의사항', content)}
                  style={{ padding: 0, height: 'auto', color: '#3B82F6', fontSize: '12px' }}
                >
                  보기
                </a-button>
              </a-tooltip>
            );
          },
        },
        {
          key: 'defaultWarranty',
          title: () => (
            <a-tooltip placement="top">
              {{
                title: () => (
                  <div style={{ fontSize: '12px', lineHeight: 1.5 }}>
                    <div><strong>데이터 출처:</strong> items 컬렉션</div>
                    <div style={{ marginTop: '4px' }}>병원이 contentstemplates 컬렉션에 별도 템플릿을 등록하지 않으면 이 값이 기본값으로 사용됩니다.</div>
                  </div>
                ),
                default: () => (
                  <span style={{ cursor: 'help', borderBottom: '1px dashed #9CA3AF', whiteSpace: 'nowrap' }}>
                    보증사항 <i class="fa-solid fa-circle-question" style={{ color: '#9CA3AF', marginLeft: '2px', fontSize: '11px' }}></i>
                  </span>
                ),
              }}
            </a-tooltip>
          ),
          dataIndex: 'defaultWarranty',
          align: 'start',
          width: 100,
          customRender: ({ record }) => {
            const content = record.defaultWarranty;
            if (!content) {
              return <span style={{ color: '#9CA3AF' }}>-</span>;
            }
            return (
              <a-tooltip title={content.length > 50 ? content.substring(0, 50) + '...' : content}>
                <a-button
                  type="link"
                  size="small"
                  onClick={() => this.openTemplateModal('병원 보증사항', content)}
                  style={{ padding: 0, height: 'auto', color: '#3B82F6', fontSize: '12px' }}
                >
                  보기
                </a-button>
              </a-tooltip>
            );
          },
        },
        {
          key: 'warrantyStatus',
          title: '무등록',
          dataIndex: 'warrantyStatus',
          align: 'center',
          width: 60,
          customRender: ({ record }) => {
            return (
              <span style={{ color: record.unregisterYn === 'Y' ? '#10B981' : '#9CA3AF' }}>
                {record.unregisterYn === 'Y' ? 'O' : 'X'}
              </span>
            );
          },
        },
        {
          key: 'duplicateYn',
          title: '중복',
          dataIndex: 'duplicateYn',
          align: 'center',
          width: 60,
          customRender: ({ record }) => {
            return (
              <span style={{ color: record.duplicateYn === 'Y' ? '#10B981' : '#9CA3AF' }}>
                {record.duplicateYn === 'Y' ? 'O' : 'X'}
              </span>
            );
          },
        },
        {
          key: 'hospitalCount',
          title: '병원',
          dataIndex: 'hospitalCount',
          align: 'center',
          width: 60,
          customRender: ({ record }) => this.formatCommaNumber(record.hospitalCount || 0),
        },
        {
          key: 'actions',
          title: '관리',
          dataIndex: '_id',
          align: 'center',
          width: 110,
          customRender: ({ record }) => {
            return (
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <a-button
                  shape="circle"
                  onClick={() => this.handleManageHospitals(record._id, record.itemName)}
                  title="병원 권한 관리"
                  icon={<i class="fa-solid fa-hospital" />}
                />
                <a-button
                  shape="circle"
                  onClick={() => this.handleEditItem(record._id)}
                  icon={<i class="fa-regular fa-pen-to-square" />}
                />
                <a-button
                  shape="circle"
                  onClick={() => this.handleDeleteItem(record._id)}
                  icon={<i class="fa-solid fa-trash-can" />}
                />
              </div>
            );
          },
        },
      ],
    };
  },
  async mounted() {
    await this.getManufacturers();
  },
  methods: {
    // 제조사 목록 조회
    async getManufacturers() {
      try {
        const response = await this.$apiInstance.post('/companies/certi-admin/category-bind/manufacturers', {
          page: 1,
          limit: 100,
        });
        if (response.status === 200 || response.status === 201) {
          this.manufacturers = response.data?.docs || response.data || [];

          // 자이스코리아를 기본값으로 설정
          const zeiss = this.manufacturers.find(m => m.name === '자이스코리아');
          if (zeiss) {
            this.selectedManufacturer = zeiss._id;
            await this.getItemsList();
          } else if (this.manufacturers.length > 0) {
            // 자이스코리아가 없으면 첫 번째 제조사 선택
            this.selectedManufacturer = this.manufacturers[0]._id;
            await this.getItemsList();
          }
        }
      } catch (error) {
        console.error('제조사 목록 조회 실패:', error);
        await oneBtnDialog('제조사 목록 조회 실패', '제조사 목록을 불러오는데 실패했습니다.', false);
      }
    },

    // 제조사 변경 핸들러
    async handleManufacturerChange() {
      this.paginationData.currentPage = 1;
      await this.getItemsList();
    },

    // 품목 리스트 조회
    async getItemsList() {
      if (!this.selectedManufacturer) return;

      // 선택된 제조사의 이름을 가져옴
      const manufacturer = this.manufacturers.find(m => m._id === this.selectedManufacturer);
      if (!manufacturer) return;

      const params = {
        page: this.paginationData.currentPage,
        limit: this.rowsPerPage,
        companyName: manufacturer.name,
      };

      if (this.searchFilter && this.searchCondition) {
        params.search = this.searchFilter;
        params.condition = this.searchCondition;
      }

      try {
        const response = await this.$apiInstance.get('/items/by-company-name', { params });
        if (response.status === 200 || response.status === 201) {
          this.calledApiTimes++;

          if (response.data.docs) {
            this.responseData = response.data.docs;
          } else {
            this.responseData = [];
          }

          this.listSize = response.data.totalDocs || 0;

          // 인덱스 계산
          for (let i = 0; i < this.responseData.length; i++) {
            this.responseData[i].index = this.listSize - (this.paginationData.currentPage - 1) * this.rowsPerPage - i;
          }

          this.paginationData.totalPages = response.data.totalPages || 0;
        }
      } catch (error) {
        console.error('품목 목록 조회 실패:', error);
        await oneBtnDialog('품목 목록 조회 실패', '품목 목록을 불러오는데 실패했습니다.', false);
      }
    },

    // 페이지 변경 핸들러
    async handlePageChange(page) {
      this.paginationData.currentPage = page;
      await this.getItemsList();
    },

    // 페이지 당 행 수 변경
    async handleRowsPerPage() {
      this.paginationData.currentPage = 1;
      await this.getItemsList();
    },

    // 검색 핸들러
    async handleSearch() {
      if (this.searchFilter === '' && this.searchCondition) {
        await oneBtnDialog('검색 필터 미선택', '검색 필터를 선택해 주세요.', false);
        return;
      }
      this.paginationData.currentPage = 1;
      await this.getItemsList();
    },

    // 검색 초기화 핸들러
    async handleReset() {
      this.searchFilter = undefined;
      this.searchCondition = '';
      this.paginationData.currentPage = 1;
      await this.getItemsList();
    },

    // 기본 템플릿 내용 모달 열기
    openTemplateModal(title, content) {
      this.templateModalTitle = title;
      this.templateModalContent = content;
      this.templateModalVisible = true;
    },

    // 기본 템플릿 내용 모달 닫기
    closeTemplateModal() {
      this.templateModalVisible = false;
      this.templateModalTitle = '';
      this.templateModalContent = '';
    },

    // 시술 등록 페이지로 이동
    handleAddItem() {
      const manufacturer = this.manufacturers.find(m => m._id === this.selectedManufacturer);
      this.$router.push(`/item/add?companyName=${manufacturer?.name || ''}`);
    },

    // 시술 수정 페이지로 이동
    handleEditItem(itemId) {
      const manufacturer = this.manufacturers.find(m => m._id === this.selectedManufacturer);
      this.$router.push({
        name: 'ModifyItem',
        params: { itemId },
        query: { companyName: manufacturer?.name || '' },
      });
    },

    // 시술 삭제
    async handleDeleteItem(itemId) {
      // TODO: 삭제 로직 구현
      console.log('Delete item:', itemId);
    },

    // ========== 순서 관리 모드 ==========
    // 순서 관리 모드 진입 (전체 데이터 로드)
    async enterOrderMode() {
      if (!this.selectedManufacturer) {
        this.$message.warning('제조사를 선택해주세요.');
        return;
      }

      try {
        const manufacturer = this.manufacturers.find(m => m._id === this.selectedManufacturer);
        if (!manufacturer) return;

        // 전체 데이터 로드 (displayOrder 순으로 정렬)
        const response = await this.$apiInstance.get('/items/by-company-name', {
          params: {
            companyName: manufacturer.name,
            page: 1,
            limit: 1000, // 전체 데이터 로드
          },
        });

        if (response.status === 200 || response.status === 201) {
          // displayOrder 순으로 정렬 (낮을수록 먼저)
          const items = response.data.docs || [];
          this.orderModeData = items.sort((a, b) =>
            (a.displayOrder ?? 100) - (b.displayOrder ?? 100),
          );
          this.isOrderMode = true;
        }
      } catch (error) {
        console.error('Failed to load items for order mode:', error);
        this.$message.error('데이터 로드에 실패했습니다.');
      }
    },

    // 순서 관리 모드 취소
    cancelOrderMode() {
      this.isOrderMode = false;
      this.orderModeData = [];
      this.draggingItem = null;
    },

    // 드래그 시작
    onDragStart(event) {
      this.draggingItem = this.orderModeData[event.oldIndex]?._id;
    },

    // 드래그 종료
    onDragEnd() {
      this.draggingItem = null;
    },

    // 순서 저장
    async saveDisplayOrder() {
      if (this.orderModeData.length === 0) {
        this.$message.warning('저장할 데이터가 없습니다.');
        return;
      }

      try {
        this.orderSaving = true;

        // 새로운 displayOrder 계산 (드래그 순서 기준으로 1, 2, 3...)
        const items = this.orderModeData.map((item, index) => ({
          itemId: item._id,
          displayOrder: index + 1,
        }));

        const response = await this.$apiInstance.put('/items/bulk-update/display-order', {
          items,
        });

        if (response.status === 200 || response.status === 201) {
          this.$message.success(response.data?.message || '순서가 저장되었습니다.');
          this.cancelOrderMode();
          // 목록 새로고침
          await this.getItemsList();
        }
      } catch (error) {
        console.error('Failed to save display order:', error);
        this.$message.error('순서 저장에 실패했습니다.');
      } finally {
        this.orderSaving = false;
      }
    },

    // 병원 권한 관리 모달 열기
    async handleManageHospitals(itemId, itemName) {
      this.selectedItemId = itemId;
      this.selectedItemName = itemName;

      // Poka-Yoke: 선택된 아이템의 기본 템플릿 등록 여부 확인
      const selectedItem = this.responseData.find(item => item._id === itemId);
      this.selectedItemHasDefaultTemplate = !!(selectedItem?.defaultAfterSurgery);

      this.hospitalModalVisible = true;
      await this.loadAvailableHospitals();
    },

    // 권한 부여 가능한 병원 목록 조회 (페이지네이션 지원)
    async loadAvailableHospitals(page = 1) {
      try {
        this.hospitalModalLoading = true;
        const response = await this.$apiInstance.get('/companies/hospitals-for-item', {
          params: {
            itemId: this.selectedItemId,
            page,
            limit: this.hospitalPagination.limit,
            search: this.hospitalSearchKeyword || undefined,
          },
        });
        if (response.status === 200 || response.status === 201) {
          this.availableHospitals = response.data?.data || [];
          this.hospitalPagination = {
            page: response.data?.page || 1,
            limit: response.data?.limit || 10,
            total: response.data?.total || 0,
            totalPages: response.data?.totalPages || 0,
          };
        }
      } catch (error) {
        console.error('Failed to load hospitals:', error);
        this.$message.error('병원 목록 조회에 실패했습니다.');
      } finally {
        this.hospitalModalLoading = false;
      }
    },

    // 병원 검색
    handleHospitalSearch() {
      this.hospitalPagination.page = 1;
      this.loadAvailableHospitals(1);
    },

    // 병원 페이지 변경
    handleHospitalPageChange(page) {
      this.loadAvailableHospitals(page);
    },

    // 병원 선택 변경 (Map 기반으로 페이지/검색 간 선택 유지)
    handleHospitalSelectionChange(selectedRowKeys) {
      // 현재 페이지의 병원들에 대해서만 선택 상태 업데이트
      const currentPageIds = this.availableHospitals.map(h => h._id);

      // 현재 페이지에서 선택 해제된 항목 제거
      currentPageIds.forEach(id => {
        if (!selectedRowKeys.includes(id)) {
          this.selectedHospitalsMap.delete(id);
        }
      });

      // 새로 선택된 항목 추가
      selectedRowKeys.forEach(id => {
        if (!this.selectedHospitalsMap.has(id)) {
          const hospital = this.availableHospitals.find(h => h._id === id);
          if (hospital) {
            this.selectedHospitalsMap.set(id, {
              _id: hospital._id,
              hospitalId: hospital.hospitalId,
              name: hospital.name,
              cNo: hospital.cNo,
            });
          }
        }
      });

      // Vue 반응성을 위해 Map을 새로 할당
      this.selectedHospitalsMap = new Map(this.selectedHospitalsMap);
    },

    // 선택된 병원 개별 제거
    removeSelectedHospital(id) {
      this.selectedHospitalsMap.delete(id);
      this.selectedHospitalsMap = new Map(this.selectedHospitalsMap);
    },

    // 선택된 병원 전체 해제
    clearAllSelectedHospitals() {
      this.selectedHospitalsMap = new Map();
    },

    // 병원 권한 부여
    async submitHospitalPermissions() {
      if (this.selectedHospitalsMap.size === 0) {
        this.$message.warning('권한을 부여할 병원을 선택해주세요.');
        return;
      }

      try {
        this.hospitalModalLoading = true;
        const hospitalData = Array.from(this.selectedHospitalsMap.values()).map(h => ({
          hospitalId: h.hospitalId,
          itemId: this.selectedItemId,
          manufacturerId: this.selectedManufacturer,
        }));

        const response = await this.$apiInstance.post(
          '/item-hospitals/bulk',
          { hospitals: hospitalData },
        );

        if (response.status === 200 || response.status === 201) {
          const result = response.data?.data;
          const message = response.data?.message || `${result?.success || 0}개 병원에 권한을 부여했습니다.`;

          // 성공/스킵에 따른 메시지 타입 결정
          if (result?.success > 0) {
            this.$message.success(message);
          } else if (result?.skipped > 0) {
            this.$message.warning(message);
          } else {
            this.$message.info(message);
          }

          this.closeHospitalModal();
          await this.getItemsList();
        }
      } catch (error) {
        console.error('Failed to grant hospital permissions:', error);
        this.$message.error('권한 부여 중 오류가 발생했습니다.');
      } finally {
        this.hospitalModalLoading = false;
      }
    },

    // 병원 권한 관리 모달 닫기
    closeHospitalModal() {
      this.hospitalModalVisible = false;
      this.hospitalModalTab = 'grant';
      this.selectedItemId = null;
      this.selectedItemName = '';
      this.selectedItemHasDefaultTemplate = false;
      // 권한 부여 탭 초기화
      this.availableHospitals = [];
      this.selectedHospitalsMap = new Map();
      this.hospitalSearchKeyword = '';
      this.hospitalPagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      };
      // 권한 관리 탭 초기화
      this.permittedHospitals = [];
      this.selectedPermittedHospitals = [];
      this.selectedPermittedIds = [];
      this.permittedSearchKeyword = '';
      this.permittedPagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      };
    },

    // 탭 변경 핸들러
    handleHospitalTabChange(activeKey) {
      if (activeKey === 'grant') {
        this.loadAvailableHospitals(1);
      } else if (activeKey === 'manage') {
        this.loadPermittedHospitals(1);
      }
    },

    // 권한 있는 병원 목록 조회
    async loadPermittedHospitals(page = 1) {
      try {
        this.hospitalModalLoading = true;
        const response = await this.$apiInstance.get(`/item-hospitals/by-item/${this.selectedItemId}`, {
          params: {
            page,
            limit: this.permittedPagination.limit,
          },
        });
        if (response.status === 200 || response.status === 201) {
          const data = response.data?.data || [];
          // 병원 정보가 포함된 응답 데이터 매핑
          this.permittedHospitals = data.map(item => ({
            _id: item._id,
            hospitalId: item.hospitalId,
            hospitalName: item.hospitalName || '-',
            cNo: item.cNo || '-',
          }));
          // 페이지네이션 정보 매핑
          this.permittedPagination = {
            page: response.data?.page || 1,
            limit: response.data?.limit || 10,
            total: response.data?.totalCount || 0,
            totalPages: response.data?.totalPages || 0,
          };
        }
      } catch (error) {
        console.error('Failed to load permitted hospitals:', error);
        this.$message.error('권한 병원 목록 조회에 실패했습니다.');
      } finally {
        this.hospitalModalLoading = false;
      }
    },

    // 권한 관리 탭 검색
    handlePermittedSearch() {
      this.permittedPagination.page = 1;
      this.loadPermittedHospitals(1);
    },

    // 권한 관리 탭 페이지 변경
    handlePermittedPageChange(page) {
      this.loadPermittedHospitals(page);
    },

    // 권한 관리 탭 선택 변경
    handlePermittedSelectionChange(selectedRowKeys) {
      this.selectedPermittedIds = selectedRowKeys;
      this.selectedPermittedHospitals = this.permittedHospitals.filter(h =>
        selectedRowKeys.includes(h._id),
      );
    },

    // 병원 권한 삭제
    async removeHospitalPermissions() {
      if (this.selectedPermittedIds.length === 0) {
        this.$message.warning('삭제할 병원을 선택해주세요.');
        return;
      }

      try {
        this.hospitalModalLoading = true;
        const response = await this.$apiInstance.post(
          '/item-hospitals/bulk-delete',
          { ids: this.selectedPermittedIds },
        );

        if (response.status === 200 || response.status === 201) {
          const result = response.data?.data;
          this.$message.success(
            `${result?.deletedCount || 0}개 병원의 권한을 삭제했습니다.`,
          );
          // 권한 관리 목록 새로고침
          this.selectedPermittedIds = [];
          this.selectedPermittedHospitals = [];
          await this.loadPermittedHospitals(1);
          await this.getItemsList();
        }
      } catch (error) {
        console.error('Failed to remove hospital permissions:', error);
        this.$message.error('권한 삭제 중 오류가 발생했습니다.');
      } finally {
        this.hospitalModalLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/common-css.scss';

.row {
  padding: 28px !important;
}

// 페이지 타이틀 섹션
.page-title-section {
  margin-bottom: 24px;

  .title-content {
    h3 {
      font-size: 24px;
      font-weight: 600;
      color: #17191C;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 8px;

      .help-icon {
        font-size: 16px;
        color: #9CA3AF;
        cursor: help;
        transition: color 0.2s ease;

        &:hover {
          color: #ffe414;
        }
      }
    }

    h5 {
      font-size: 14px;
      color: #6B7280;
      margin: 0;
    }
  }
}

// 페이지 도움말 툴팁
:global(.page-help-tooltip) {
  .ant-tooltip-inner {
    max-width: 400px;
    padding: 0;
    background-color: #1F2937;
  }

  .tooltip-content {
    padding: 16px;

    .tooltip-section {
      &:not(:last-child) {
        margin-bottom: 16px;
        padding-bottom: 16px;
        border-bottom: 1px solid #374151;
      }

      .tooltip-title {
        font-size: 13px;
        font-weight: 600;
        color: #ffe414;
        margin-bottom: 8px;
      }

      .tooltip-body {
        font-size: 12px;
        color: #E5E7EB;
        line-height: 1.6;

        code {
          background-color: #374151;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'Monaco', 'Consolas', monospace;
          color: #10B981;
        }

        ul {
          margin: 0;
          padding-left: 16px;

          li {
            margin-bottom: 4px;

            &:last-child {
              margin-bottom: 0;
            }
          }
        }

        strong {
          color: #ffffff;
        }
      }
    }
  }
}

// 필터 섹션
.filter-section {
  background-color: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;

  .filter-row {
    margin-bottom: 16px;

    .filter-content {
      display: flex;
      align-items: center;
      gap: 24px;
      flex-wrap: wrap;

      .filter-item {
        display: flex;
        align-items: center;
        gap: 12px;

        .filter-label {
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          white-space: nowrap;
        }

        .search-box {
          display: flex;
          align-items: center;
          gap: 0;

          .search-filter-select {
            :deep(.ant-select-selector) {
              border-radius: 6px 0 0 6px !important;
            }
          }

          .search-input {
            border-radius: 0 6px 6px 0 !important;
            border-left: none;
          }
        }
      }
    }
  }

  .filter-actions {
    display: flex;
    justify-content: flex-start;
    gap: 8px;
  }
}

// 컨트롤 섹션
.control-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  margin-bottom: 16px;
  border-bottom: 1px solid #E5E7EB;

  .control-left {
    .total-count {
      font-size: 14px;
      color: #6B7280;
      margin: 0;

      .number-highlight {
        font-weight: 600;
        color: #17191C;
      }
    }
  }

  .control-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

// 조회 섹션
.list-section {
  background-color: white;
  border-radius: 8px;

  .noData {
    padding: 60px 0;
    text-align: center;
    color: #9CA3AF;
  }

  // 페이지네이션 영역
  .pagination-section {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    padding: 20px 0;

    .rows-per-page-select {
      width: 130px;
      margin-top: -5px;

      :deep(.ant-select-selector) {
        height: 32px !important;
        display: flex;
        align-items: center;
      }
    }
  }
}

// 버튼 스타일
.ant-btn {
  border-radius: 6px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.ant-btn-primary {
  background-color: #ffe414;
  border-color: #ffe414;
  color: #17191C;

  &:hover {
    background-color: #ffd700;
    border-color: #ffd700;
  }
}

// 상태 표시 스타일
.status-wrap-align {
  display: flex;
  justify-content: center;

  .status-wrap {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
  }
}

// 병원 권한 모달 스타일
.hospital-modal-content {
  padding: 0 20px 20px 20px;

  .modal-header {
    margin-bottom: 16px;

    p {
      margin: 0;
      color: #374151;
      font-size: 14px;
    }
  }

  // 선택된 병원 섹션
  .selected-hospitals-section {
    background-color: #FEF9E7;
    border: 1px solid #F7DC6F;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;

    .selected-hospitals-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      .selected-title {
        font-size: 14px;
        font-weight: 600;
        color: #6B4E00;

        i {
          color: #F39C12;
          margin-right: 6px;
        }
      }
    }

    .selected-hospitals-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      max-height: 120px;
      overflow-y: auto;

      .selected-hospital-tag {
        background-color: #FFFFFF;
        border: 1px solid #E5E7EB;
        border-radius: 4px;
        padding: 4px 8px;
        font-size: 13px;
        color: #374151;

        :deep(.ant-tag-close-icon) {
          color: #9CA3AF;
          margin-left: 4px;

          &:hover {
            color: #EF4444;
          }
        }
      }
    }
  }

  .hospital-search {
    margin-bottom: 16px;

    :deep(.ant-input-search) {
      .ant-input-wrapper {
        .ant-input-affix-wrapper {
          height: 40px;
          padding: 8px 11px;

          .ant-input {
            height: auto;
          }
        }
        .ant-input-group-addon {
          .ant-input-search-button {
            height: 40px;
            width: 40px;
          }
        }
      }
    }
  }

  .hospital-selection {
    margin-bottom: 16px;
  }

  .hospital-pagination {
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
  }

  .modal-summary {
    padding: 12px 16px;
    background-color: #F9FAFB;
    border-radius: 6px;

    p {
      margin: 0;
      font-size: 14px;
      color: #374151;
    }
  }
}

// 모달 푸터 버튼 스타일
.modal-footer-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 20px 20px 20px;
}

// yellow-button 스타일 (모달 내부용)
.yellow-button {
  background-color: #ffe414 !important;
  border-color: #ffe414 !important;
  color: #17191C !important;

  &:hover {
    background-color: #ffd700 !important;
    border-color: #ffd700 !important;
  }
}

// 기본 템플릿 모달 스타일
.template-modal-content {
  .template-info-box {
    background-color: #F0F9FF;
    border: 1px solid #BAE6FD;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;

    .info-item {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      font-size: 13px;
      color: #0369A1;
      line-height: 1.5;

      &:not(:last-child) {
        margin-bottom: 8px;
      }

      i {
        margin-top: 2px;
        flex-shrink: 0;
      }

      strong {
        font-weight: 600;
      }
    }
  }

  .template-content-box {
    background-color: #F9FAFB;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    padding: 16px;
    max-height: 400px;
    overflow-y: auto;

    .template-text {
      margin: 0;
      font-size: 14px;
      color: #374151;
      line-height: 1.8;
      white-space: pre-wrap;
      word-break: break-word;
      font-family: inherit;
    }
  }
}

// 순서 관리 모드 스타일
.order-mode-container {
  background-color: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 16px;

  .order-mode-header {
    margin-bottom: 16px;

    .order-mode-info {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      background-color: #FEF3C7;
      border: 1px solid #F59E0B;
      border-radius: 6px;
      color: #92400E;
      font-size: 14px;
      margin: 0;

      i {
        color: #F59E0B;
      }
    }
  }

  .draggable-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .draggable-item {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    background-color: #F9FAFB;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    cursor: default;
    transition: all 0.2s ease;

    &:hover {
      background-color: #F3F4F6;
      border-color: #D1D5DB;
    }

    &.is-dragging {
      background-color: #FEF9E7;
      border-color: #F7DC6F;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .drag-handle {
      cursor: grab;
      color: #9CA3AF;
      padding: 8px;
      margin: -8px;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        color: #6B7280;
      }

      &:active {
        cursor: grabbing;
      }

      i {
        font-size: 16px;
      }
    }

    .item-order {
      width: 32px;
      height: 32px;
      background-color: #ffe414;
      color: #17191C;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 14px;
      flex-shrink: 0;
    }

    .item-image {
      width: 40px;
      height: 50px;
      border-radius: 4px;
      overflow: hidden;
      flex-shrink: 0;
      background-color: #E5E7EB;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .no-image {
        color: #9CA3AF;
        font-size: 12px;
      }
    }

    .item-info {
      flex: 1;
      min-width: 0;

      .item-name {
        font-size: 14px;
        font-weight: 600;
        color: #17191C;
        margin-bottom: 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .item-meta {
        display: flex;
        gap: 12px;

        .item-code {
          font-size: 12px;
          color: #6B7280;
        }

        .item-type {
          font-size: 12px;
          color: #3B82F6;
          background-color: #EFF6FF;
          padding: 2px 8px;
          border-radius: 4px;
        }
      }
    }

    .item-display-order {
      font-size: 12px;
      color: #9CA3AF;
      padding: 4px 8px;
      background-color: #F3F4F6;
      border-radius: 4px;
      flex-shrink: 0;
    }
  }
}

// 반응형 스타일
@media screen and (max-width: 1023px) {
  .control-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;

    .control-right {
      width: 100%;
      flex-wrap: wrap;
    }
  }

  .filter-section .filter-content {
    flex-wrap: wrap;
  }
}

@media screen and (max-width: 767px) {
  .row {
    padding: 14px !important;
  }

  .control-section .control-right {
    .search-box {
      width: 100%;

      .search-input {
        flex: 1;
      }
    }
  }
}
</style>
